<template>
  <div class="coupon-issue-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <el-button @click="$router.go(-1)" class="back-btn" size="medium">
            <i class="el-icon-arrow-left"></i> 返回
          </el-button>
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-s-promotion"></i>
              优惠券发放
            </h1>
            <p class="page-subtitle">选择模板，配置发放参数，向用户发放优惠券</p>
          </div>
        </div>
        <div class="header-actions" v-if="selectedTemplate">
          <div class="template-badge">
            <i class="el-icon-present"></i>
            已选择：{{ selectedTemplate.coupon_name }}
          </div>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-container">
      <div class="form-section">
        <!-- 模板选择卡片 -->
        <div class="form-card">
          <div class="card-header">
            <h3>
              <i class="el-icon-tickets"></i>
              选择优惠券模板
            </h3>
            <p>从已有的模板中选择要发放的优惠券</p>
          </div>
          <div class="card-content">
            <el-select 
              v-model="selectedTemplateId" 
              placeholder="请选择优惠券模板"
              @change="handleTemplateChange"
              size="large"
              style="width: 100%"
            >
              <el-option
                v-for="template in templateList"
                :key="template.id"
                :label="`${template.coupon_name} (${formatDiscountRate(template.discount_rate)})`"
                :value="template.id"
              >
                <div class="template-option">
                  <div class="template-name">{{ template.coupon_name }}</div>
                  <div class="template-info">
                    <span class="discount">{{ formatDiscountRate(template.discount_rate) }}</span>
                    <span class="validity">{{ template.valid_days }}天有效</span>
                    <span class="stock">剩余: {{ getTemplateStock(template) }}</span>
                  </div>
                </div>
              </el-option>
            </el-select>
          </div>
        </div>

        <!-- 发放方式选择 -->
        <div class="form-card">
          <div class="card-header">
            <h3>
              <i class="el-icon-s-operation"></i>
              发放方式
            </h3>
            <p>选择单个发放或批量发放</p>
          </div>
          <div class="card-content">
            <div class="issue-type-selector">
              <div 
                class="issue-type-option"
                :class="{ active: issueType === 'single' }"
                @click="handleIssueTypeChange('single')"
              >
                <div class="option-icon">
                  <i class="el-icon-user"></i>
                </div>
                <div class="option-content">
                  <div class="option-title">单个发放</div>
                  <div class="option-desc">向指定用户发放优惠券</div>
                </div>
              </div>
              <div 
                class="issue-type-option"
                :class="{ active: issueType === 'batch' }"
                @click="handleIssueTypeChange('batch')"
              >
                <div class="option-icon">
                  <i class="el-icon-user-solid"></i>
                </div>
                <div class="option-content">
                  <div class="option-title">批量发放</div>
                  <div class="option-desc">向多个用户批量发放优惠券</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 单个发放表单 -->
        <div v-if="issueType === 'single'" class="form-card">
          <div class="card-header">
            <h3>
              <i class="el-icon-user"></i>
              单个发放设置
            </h3>
            <p>配置单个用户的发放信息</p>
          </div>
          <div class="card-content">
            <el-form ref="singleForm" :model="singleForm" :rules="singleRules" label-width="120px">
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="用户ID" prop="user_id">
                    <el-input-number 
                      v-model="singleForm.user_id" 
                      :min="1" 
                      style="width: 100%"
                      size="large"
                      placeholder="请输入用户ID"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="发放来源" prop="source_type">
                    <el-select v-model="singleForm.source_type" placeholder="请选择发放来源" style="width: 100%" size="large">
                      <el-option
                        v-for="source in sourceTypeOptions"
                        :key="source.value"
                        :label="source.label"
                        :value="source.value"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>

              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="关联ID">
                    <el-input-number 
                      v-model="singleForm.source_id" 
                      :min="0" 
                      style="width: 100%"
                      size="large"
                      placeholder="可选，关联活动或订单ID"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="自定义有效期">
                    <el-input-number 
                      v-model="singleForm.custom_expire_days" 
                      :min="1" 
                      :max="365"
                      style="width: 100%"
                      size="large"
                      placeholder="可选，覆盖模板设置"
                    >
                      <template slot="append">天</template>
                    </el-input-number>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
          </div>
        </div>

        <!-- 批量发放表单 -->
        <div v-if="issueType === 'batch'" class="form-card">
          <div class="card-header">
            <h3>
              <i class="el-icon-user-solid"></i>
              批量发放设置
            </h3>
            <p>配置批量用户的发放信息</p>
          </div>
          <div class="card-content">
            <el-form ref="batchForm" :model="batchForm" :rules="batchRules" label-width="120px">
              <el-form-item label="用户ID列表" prop="user_ids">
                <el-input
                  v-model="userIdsInput"
                  type="textarea"
                  :rows="6"
                  placeholder="请输入用户ID，每行一个或用逗号分隔&#10;例如：&#10;12345&#10;12346&#10;12347&#10;或：12345,12346,12347"
                  @input="handleUserIdsInput"
                />
                <div class="input-stats">
                  <div class="stats-left">
                    <span class="user-count">已输入 {{ batchForm.user_ids.length }} 个用户ID</span>
                  </div>
                  <div class="stats-right">
                    <el-button type="text" @click="clearUserIds" icon="el-icon-delete">清空</el-button>
                  </div>
                </div>
              </el-form-item>

              <el-row :gutter="20">
                <el-col :span="8">
                  <el-form-item label="发放来源" prop="source_type">
                    <el-select v-model="batchForm.source_type" placeholder="请选择发放来源" style="width: 100%" size="large">
                      <el-option
                        v-for="source in sourceTypeOptions"
                        :key="source.value"
                        :label="source.label"
                        :value="source.value"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="关联ID">
                    <el-input-number 
                      v-model="batchForm.source_id" 
                      :min="0" 
                      style="width: 100%"
                      size="large"
                      placeholder="可选"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item label="自定义有效期">
                    <el-input-number 
                      v-model="batchForm.custom_expire_days" 
                      :min="1" 
                      :max="365"
                      style="width: 100%"
                      size="large"
                      placeholder="可选"
                    >
                      <template slot="append">天</template>
                    </el-input-number>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-form>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="form-actions">
          <el-button @click="resetForm" size="large">
            <i class="el-icon-refresh"></i> 重置
          </el-button>
          <el-button 
            v-if="issueType === 'single'"
            type="primary" 
            @click="handleSingleIssue" 
            :loading="issuing"
            size="large"
          >
            <i class="el-icon-s-promotion"></i> 发放优惠券
          </el-button>
          <el-button 
            v-if="issueType === 'batch'"
            type="primary" 
            @click="handleBatchIssue" 
            :loading="issuing"
            size="large"
          >
            <i class="el-icon-s-promotion"></i> 批量发放优惠券
          </el-button>
        </div>
      </div>

      <!-- 右侧预览区域 -->
      <div class="preview-section">
        <!-- 模板预览 -->
        <div v-if="selectedTemplate" class="preview-card">
          <div class="coupon-preview">
            <div class="coupon-card">
              <div class="coupon-header">
                <div class="coupon-discount">
                  {{ getDiscountText() }}
                </div>
                <div class="coupon-type">优惠券</div>
              </div>
              <div class="coupon-body">
                <div class="coupon-title">{{ selectedTemplate.coupon_name }}</div>
                <div class="coupon-condition">
                  {{ getConditionText() }}
                </div>
              </div>
              <div class="coupon-footer">
                <div class="validity">有效期 {{ selectedTemplate.valid_days }}天</div>
                <div class="usage">每人限用{{ selectedTemplate.usage_limit }}次</div>
              </div>
            </div>
          </div>

          <div class="template-details">
            <h4>
              <i class="el-icon-info"></i>
              模板信息
            </h4>
            <div class="detail-grid">
              <div class="detail-item">
                <div class="detail-label">折扣率</div>
                <div class="detail-value discount">{{ formatDiscountRate(selectedTemplate.discount_rate) }}</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">使用门槛</div>
                <div class="detail-value">{{ selectedTemplate.min_amount > 0 ? `满${selectedTemplate.min_amount}元` : '无门槛' }}</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">有效天数</div>
                <div class="detail-value">{{ selectedTemplate.valid_days }}天</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">使用次数</div>
                <div class="detail-value">每人{{ selectedTemplate.usage_limit }}次</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">剩余数量</div>
                <div class="detail-value">{{ getTemplateStock(selectedTemplate) }}</div>
              </div>
              <div class="detail-item">
                <div class="detail-label">状态</div>
                <div class="detail-value">
                  <el-tag type="success" size="mini">已启用</el-tag>
                </div>
              </div>
            </div>
          </div>

          <!-- 发放统计 -->
          <div v-if="issueType === 'batch' && batchForm.user_ids.length > 0" class="issue-stats">
            <h4>
              <i class="el-icon-data-analysis"></i>
              发放预览
            </h4>
            <div class="stats-grid">
              <div class="stat-item">
                <div class="stat-value">{{ batchForm.user_ids.length }}</div>
                <div class="stat-label">待发放用户</div>
              </div>
              <div class="stat-item">
                <div class="stat-value">{{ getEstimatedCost() }}</div>
                <div class="stat-label">预估成本</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-else class="empty-preview">
          <div class="empty-icon">
            <i class="el-icon-present"></i>
          </div>
          <div class="empty-text">
            <h4>请选择优惠券模板</h4>
            <p>选择模板后将在此处显示预览</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 发放结果对话框 -->
    <el-dialog 
      :title="issueType === 'single' ? '发放成功' : '批量发放完成'"
      :visible.sync="showResult" 
      width="600px"
      center
    >
      <div v-if="issueResult" class="result-content">
        <!-- 单个发放结果 -->
        <div v-if="issueType === 'single'" class="single-result">
          <div class="result-success">
            <i class="el-icon-success result-icon"></i>
            <h3>优惠券发放成功！</h3>
          </div>

          <div class="result-details">
            <div class="result-item">
              <label>优惠券ID：</label>
              <span>{{ issueResult.coupon_id }}</span>
            </div>
            <div class="result-item">
              <label>优惠券码：</label>
              <span class="coupon-code">{{ issueResult.coupon_code }}</span>
            </div>
            <div class="result-item">
              <label>二维码：</label>
              <span>{{ issueResult.qr_code }}</span>
            </div>
            <div class="result-item">
              <label>过期时间：</label>
              <span>{{ formatDate(issueResult.expire_at) }}</span>
            </div>
          </div>
        </div>

        <!-- 批量发放结果 -->
        <div v-if="issueType === 'batch'" class="batch-result">
          <div class="result-success">
            <i class="el-icon-success result-icon"></i>
            <h3>批量发放完成！</h3>
          </div>

          <div class="batch-summary">
            <div class="summary-item success">
              <div class="summary-value">{{ issueResult.success_count }}</div>
              <div class="summary-label">成功发放</div>
            </div>
            <div class="summary-item total">
              <div class="summary-value">{{ issueResult.total_users }}</div>
              <div class="summary-label">总用户数</div>
            </div>
            <div class="summary-item error">
              <div class="summary-value">{{ issueResult.failed_count }}</div>
              <div class="summary-label">发放失败</div>
            </div>
          </div>

          <div v-if="issueResult.issued_coupons && issueResult.issued_coupons.length > 0" class="issued-preview">
            <h4>发放详情（前5条）</h4>
            <div class="issued-list">
              <div 
                v-for="(item, index) in issueResult.issued_coupons.slice(0, 5)" 
                :key="index"
                class="issued-item"
              >
                <div class="issued-user">用户 {{ item.user_id }}</div>
                <div class="issued-code">{{ item.coupon_code }}</div>
                <div class="issued-status">
                  <el-tag type="success" size="mini">成功</el-tag>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="continueIssue">继续发放</el-button>
        <el-button type="primary" @click="backToTemplates">返回模板列表</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { 
  getCouponTemplates, 
  issueCoupon, 
  batchIssueCoupon, 
  formatDiscountRate, 
  getSourceTypeOptions 
} from '@/api/coupon'

export default {
  name: 'CouponIssue',
  data() {
    return {
      templateList: [],
      selectedTemplateId: null,
      selectedTemplate: null,
      issueType: 'single',
      issuing: false,
      issueResult: null,
      showResult: false,
      
      sourceTypeOptions: getSourceTypeOptions(),
      
      // 单个发放表单
      singleForm: {
        user_id: null,
        source_type: 2,
        source_id: null,
        custom_expire_days: null
      },
      
      singleRules: {
        user_id: [
          { required: true, message: '请输入用户ID', trigger: 'change' },
          { type: 'number', min: 1, message: '用户ID必须大于0', trigger: 'change' }
        ],
        source_type: [
          { required: true, message: '请选择发放来源', trigger: 'change' }
        ]
      },
      
      // 批量发放表单
      batchForm: {
        user_ids: [],
        source_type: 3,
        source_id: null,
        custom_expire_days: null
      },
      
      batchRules: {
        source_type: [
          { required: true, message: '请选择发放来源', trigger: 'change' }
        ]
      },
      
      userIdsInput: ''
    }
  },
  
  created() {
    this.loadTemplates()
    
    // 如果URL中有templateId参数，自动选择
    const templateId = this.$route.query.templateId || this.$route.query.template_id
    if (templateId) {
      this.selectedTemplateId = parseInt(templateId)
    }
  },
  
  methods: {
    async loadTemplates() {
      try {
        const response = await getCouponTemplates({ 
          page_size: 1000, 
          is_active: true 
        })
        this.templateList = response.data?.list || []
        
        // 如果有预选的模板ID，找到对应模板
        if (this.selectedTemplateId) {
          this.selectedTemplate = this.templateList.find(t => t.id === this.selectedTemplateId)
        }
      } catch (error) {
        this.$message.error('获取模板列表失败')
      }
    },
    
    handleTemplateChange(templateId) {
      this.selectedTemplate = this.templateList.find(t => t.id === templateId)
      this.issueResult = null
    },
    
    handleIssueTypeChange(type) {
      this.issueType = type
      this.issueResult = null
    },
    
    handleUserIdsInput(value) {
      // 解析用户输入的ID列表
      const ids = value
        .split(/[,，\n\r\s]+/)
        .map(id => id.trim())
        .filter(id => id && /^\d+$/.test(id))
        .map(id => parseInt(id))
      
      this.batchForm.user_ids = [...new Set(ids)] // 去重
    },
    
    clearUserIds() {
      this.userIdsInput = ''
      this.batchForm.user_ids = []
    },
    
    async handleSingleIssue() {
      if (!this.selectedTemplateId) {
        this.$message.warning('请先选择优惠券模板')
        return
      }
      
      this.$refs.singleForm.validate(async (valid) => {
        if (valid) {
          this.issuing = true
          
          try {
            const response = await issueCoupon({
              ...this.singleForm,
              template_id: this.selectedTemplateId
            })
            
            this.issueResult = response.data
            this.showResult = true
            
          } catch (error) {
            this.$message.error('发放失败：' + (error.response?.data?.message || '未知错误'))
          } finally {
            this.issuing = false
          }
        }
      })
    },
    
    async handleBatchIssue() {
      if (!this.selectedTemplateId) {
        this.$message.warning('请先选择优惠券模板')
        return
      }
      
      if (this.batchForm.user_ids.length === 0) {
        this.$message.warning('请输入用户ID列表')
        return
      }
      
      this.$refs.batchForm.validate(async (valid) => {
        if (valid) {
          this.$confirm(`确定要向 ${this.batchForm.user_ids.length} 个用户批量发放优惠券吗？`, '确认发放', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(async () => {
            this.issuing = true
            
            try {
              const response = await batchIssueCoupon({
                ...this.batchForm,
                template_id: this.selectedTemplateId
              })
              
              this.issueResult = response.data
              this.showResult = true
              
            } catch (error) {
              this.$message.error('批量发放失败：' + (error.response?.data?.message || '未知错误'))
            } finally {
              this.issuing = false
            }
          })
        }
      })
    },
    
    resetForm() {
      this.issueResult = null
      this.showResult = false
      this.singleForm = {
        user_id: null,
        source_type: 2,
        source_id: null,
        custom_expire_days: null
      }
      this.batchForm = {
        user_ids: [],
        source_type: 3,
        source_id: null,
        custom_expire_days: null
      }
      this.userIdsInput = ''
      
      // 重置表单验证
      this.$nextTick(() => {
        this.$refs.singleForm?.resetFields()
        this.$refs.batchForm?.resetFields()
      })
    },
    
    continueIssue() {
      this.showResult = false
      this.resetForm()
    },
    
    backToTemplates() {
      this.$router.push('/admin/coupons/templates')
    },
    
    getTemplateStock(template) {
      if (template.total_quantity === -1) {
        return '不限'
      }
      return Math.max(0, template.total_quantity - (template.issued_quantity || 0))
    },
    
    getDiscountText() {
      if (!this.selectedTemplate) return ''
      return formatDiscountRate(this.selectedTemplate.discount_rate)
    },
    
    getConditionText() {
      if (!this.selectedTemplate) return ''
      return this.selectedTemplate.min_amount > 0 
        ? `满${this.selectedTemplate.min_amount}元可用` 
        : '无门槛使用'
    },
    
    getEstimatedCost() {
      if (!this.selectedTemplate || !this.batchForm.user_ids.length) return '¥0'
      // 假设每张优惠券成本为1元
      return `¥${this.batchForm.user_ids.length}`
    },
    
    formatDiscountRate,
    
    formatDate(date) {
      return new Date(date).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.coupon-issue-container {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  color: white;
}

.header-info {
  flex: 1;
}

.header-info .page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.header-info .page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  color: white;
}

.template-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 主容器 */
.main-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 30px;
}

/* 表单区域 */
.form-section {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.form-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  overflow: hidden;
  transition: all 0.3s ease;
}

.form-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.card-header {
  padding: 25px 30px 15px;
  border-bottom: 1px solid #f0f2f5;
}

.card-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-header p {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.card-content {
  padding: 25px 30px 30px;
}

/* 模板选项 */
.template-option {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.template-name {
  font-weight: 600;
  color: #303133;
  font-size: 15px;
}

.template-info {
  display: flex;
  gap: 15px;
  font-size: 12px;
}

.template-info .discount {
  color: #f56c6c;
  font-weight: 600;
}

.template-info .validity {
  color: #e6a23c;
}

.template-info .stock {
  color: #909399;
}

/* 发放方式选择器 */
.issue-type-selector {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.issue-type-option {
  padding: 20px;
  border: 2px solid #e4e7ed;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
}

.issue-type-option:hover {
  border-color: #409eff;
  transform: translateY(-2px);
}

.issue-type-option.active {
  border-color: #409eff;
  background: linear-gradient(135deg, #409eff10, #409eff05);
}

.option-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409eff, #40a9ff);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  font-size: 22px;
}

.option-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.option-desc {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
}

/* 输入统计 */
.input-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.user-count {
  font-size: 14px;
  color: #409eff;
  font-weight: 500;
}

/* 操作按钮 */
.form-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 20px 0;
}

/* 预览区域 */
.preview-section {
  position: sticky;
  top: 30px;
  height: fit-content;
}

.preview-card {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

/* 优惠券预览 */
.coupon-preview {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.coupon-card {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 16px;
  color: white;
  padding: 25px;
  position: relative;
  overflow: hidden;
}

.coupon-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
}

.coupon-header {
  text-align: center;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
}

.coupon-discount {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 4px;
}

.coupon-type {
  font-size: 14px;
  opacity: 0.9;
}

.coupon-body {
  text-align: center;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
}

.coupon-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}

.coupon-condition {
  font-size: 14px;
  opacity: 0.9;
}

.coupon-footer {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  opacity: 0.8;
  position: relative;
  z-index: 1;
}

/* 模板详情 */
.template-details {
  background: white;
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.template-details h4 {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 12px;
  color: #909399;
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.detail-value.discount {
  color: #f56c6c;
  font-weight: 600;
}

/* 发放统计 */
.issue-stats {
  background: white;
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.issue-stats h4 {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #409eff;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

/* 空状态 */
.empty-preview {
  background: white;
  border-radius: 20px;
  padding: 60px 30px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.empty-icon {
  font-size: 60px;
  color: #c0c4cc;
  margin-bottom: 20px;
}

.empty-text h4 {
  font-size: 18px;
  color: #303133;
  margin: 0 0 8px 0;
}

.empty-text p {
  font-size: 14px;
  color: #909399;
  margin: 0;
}

/* 结果对话框 */
.result-content {
  padding: 20px 0;
}

.result-success {
  text-align: center;
  margin-bottom: 30px;
}

.result-icon {
  font-size: 60px;
  color: #67c23a;
  margin-bottom: 15px;
}

.result-success h3 {
  font-size: 20px;
  color: #303133;
  margin: 0;
}

/* 单个发放结果 */
.result-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.result-item {
  display: flex;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.result-item label {
  font-weight: 600;
  color: #606266;
  width: 120px;
  flex-shrink: 0;
}

.coupon-code {
  font-family: monospace;
  color: #409eff;
  font-weight: 600;
  font-size: 16px;
}

/* 批量发放结果 */
.batch-summary {
  display: flex;
  justify-content: space-around;
  margin-bottom: 30px;
}

.summary-item {
  text-align: center;
  padding: 20px;
  border-radius: 12px;
  min-width: 80px;
}

.summary-item.success {
  background: linear-gradient(135deg, #67c23a20, #67c23a10);
}

.summary-item.total {
  background: linear-gradient(135deg, #409eff20, #409eff10);
}

.summary-item.error {
  background: linear-gradient(135deg, #f56c6c20, #f56c6c10);
}

.summary-value {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.summary-item.success .summary-value {
  color: #67c23a;
}

.summary-item.total .summary-value {
  color: #409eff;
}

.summary-item.error .summary-value {
  color: #f56c6c;
}

.summary-label {
  font-size: 14px;
  color: #666;
}

.issued-preview h4 {
  font-size: 16px;
  color: #303133;
  margin: 0 0 15px 0;
}

.issued-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.issued-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.issued-user {
  font-weight: 500;
  color: #303133;
}

.issued-code {
  font-family: monospace;
  font-size: 12px;
  color: #666;
  flex: 1;
  text-align: center;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-container {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .preview-section {
    position: static;
    order: -1;
  }
}

@media (max-width: 768px) {
  .page-header {
    padding: 16px 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .main-container {
    padding: 0 15px;
  }
  
  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .issue-type-selector {
    grid-template-columns: 1fr;
  }
  
  .detail-grid,
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .batch-summary {
    flex-direction: column;
    gap: 15px;
  }
}
</style> 